import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, MenuItem, Alert } from '@mui/material';
import type { EvcpdProduct, CreateProductInput } from '../../../api/evcpdProducts.api';

interface Props {
  open: boolean;
  initial?: EvcpdProduct | null;
  onClose: () => void;
  onSubmit: (input: CreateProductInput) => Promise<void>;
  error?: string | null;
}

const empty: CreateProductInput = { name: '', category: 'ac_charger', unitPrice: 0, gstPercent: 18 };

const CATEGORIES = [
  { value: 'ac_charger', label: 'AC Charger' },
  { value: 'dc_charger', label: 'DC Charger' },
  { value: 'connector', label: 'Connector' },
  { value: 'accessory', label: 'Accessory' },
  { value: 'spare', label: 'Spare' },
  { value: 'other', label: 'Other' },
];

export function ProductFormDialog({ open, initial, onClose, onSubmit, error }: Props) {
  const isEdit = Boolean(initial);
  const [form, setForm] = useState<CreateProductInput>(empty);

  useEffect(() => {
    setForm(
      initial
        ? {
            name: initial.name,
            sku: initial.sku ?? undefined,
            category: initial.category,
            capacityKw: initial.capacityKw,
            connectorType: initial.connectorType ?? undefined,
            unitPrice: initial.unitPrice,
            gstPercent: initial.gstPercent,
            hsnCode: initial.hsnCode ?? undefined,
            inventoryItemId: initial.inventoryItemId,
            status: initial.status,
          }
        : empty,
    );
  }, [initial, open]);

  const set = <K extends keyof CreateProductInput>(k: K, v: CreateProductInput[K]) => setForm((f) => ({ ...f, [k]: v }));
  const num = (v: string) => (v ? Number(v) : undefined);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? `Edit Product — ${initial?.productCode}` : 'New Product'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Name" value={form.name} onChange={(e) => set('name', e.target.value)} required fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField select label="Category" value={form.category ?? 'ac_charger'} onChange={(e) => set('category', e.target.value)} fullWidth>
              {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>
            <TextField label="SKU" value={form.sku ?? ''} onChange={(e) => set('sku', e.target.value)} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Capacity (kW)" type="number" value={form.capacityKw ?? ''} onChange={(e) => set('capacityKw', e.target.value ? Number(e.target.value) : null)} fullWidth />
            <TextField label="Connector Type" value={form.connectorType ?? ''} onChange={(e) => set('connectorType', e.target.value)} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Unit Price (₹)" type="number" value={form.unitPrice ?? ''} onChange={(e) => set('unitPrice', num(e.target.value))} fullWidth />
            <TextField label="GST %" type="number" value={form.gstPercent ?? ''} onChange={(e) => set('gstPercent', num(e.target.value))} fullWidth />
            <TextField label="HSN" value={form.hsnCode ?? ''} onChange={(e) => set('hsnCode', e.target.value)} fullWidth />
          </Stack>
          <TextField
            label="Inventory Item ID (link for stock allocation)"
            type="number"
            value={form.inventoryItemId ?? ''}
            onChange={(e) => set('inventoryItemId', e.target.value ? Number(e.target.value) : null)}
            helperText="Optional — link to an inventory item so orders can reserve & issue real stock"
            fullWidth
          />
          {isEdit && (
            <TextField select label="Status" value={form.status ?? 'active'} onChange={(e) => set('status', e.target.value)} fullWidth>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit(form)}>{isEdit ? 'Save' : 'Create'}</Button>
      </DialogActions>
    </Dialog>
  );
}
